/**
 * @internal
 * Internal renderer used by LineChart when data is present. Not part of the public API.
 * Use `LineChart` instead — it handles loading, empty, and rendered states automatically.
 */
import React, { forwardRef, RefObject, useEffect, useRef } from 'react';
import {
  extent as d3extent,
  scaleLinear as d3scaleLinear,
  scalePoint as d3scalePoint,
  scaleTime as d3scaleTime,
  line as d3line,
  ScaleTime as d3ScaleTime,
} from 'd3';

import { Flexbox, useTheme, useMessageFormatter } from '@redsift/design-system';
import intlMessages from './intl';

import { AnyScale, DotDatum, LegendItemDatum, LegendVariant, LinePointDatum, TwoCategoryData } from '../../types';
import { LineChartLegendVariant, LineChartProps } from './types';
import { StyledLineChart } from './styles';
import { useColor } from '../../hooks';
import { Axis, AxisVariant } from '../Axis';
import { Legend } from '../Legend';
import { Dot, DotVariant } from '../Dot';
import { statsBy, sizeToDimension } from './utils';
import { Line } from '../Line';
import { getSortingMethod, isValidDate, mergeLegends } from '../../utils';

interface RenderedLineChartProps extends Required<Pick<LineChartProps, 'data'>>, Omit<LineChartProps, 'data'> {}

export const RenderedLineChart = forwardRef<HTMLDivElement, RenderedLineChartProps>((props, ref) => {
  const {
    className,
    data: propsData,
    dotRole,
    disableAnimations,
    getDotAnchorProps,
    id,
    isDotSelected,
    labelDecorator,
    legendDecorator,
    legendVariant,
    legendProps,
    margins: propsMargins,
    onDotClick,
    size,
    sortingMethod,
    colorTheme,
    tooltipDecorator,
    tooltipVariant,
    xAxisVariant,
    xAxisPlacement,
    xAxisTickFormat: propsXAxisTickFormat,
    xAxisTickPadding,
    xAxisTickRotation,
    xAxisTickSize,
    xAxisTickValues,
    xAxisMinValue,
    xAxisMaxValue,
    xAxisTickRemodelling,
    yAxisVariant,
    yAxisPlacement,
    yAxisTickFormat,
    yAxisTickPadding,
    yAxisTickRotation,
    yAxisTickSize,
    yAxisTickValues,
    yAxisMinValue,
    yAxisMaxValue,
    yAxisTickRemodelling,
    dateParser,
    ...forwardedProps
  } = props;

  const cache = useRef<TwoCategoryData>();
  const theme = useTheme();

  const format = useMessageFormatter(intlMessages);

  const xScaleType: 'number' | 'string' | 'Date' | 'dateString' | undefined =
    propsData[0].key[0] instanceof Date
      ? 'Date'
      : typeof propsData[0].key[0] === 'number'
      ? 'number'
      : typeof propsData[0].key[0] === 'string' && isValidDate(propsData[0].key[0])
      ? 'dateString'
      : typeof propsData[0].key[0] === 'string'
      ? 'string'
      : undefined;
  const data =
    xScaleType === 'number'
      ? propsData.filter((datum) => typeof datum.key[0] === 'number' && !Number.isNaN(datum.key[0]))
      : xScaleType === 'dateString'
      ? propsData.filter((datum) => isValidDate(datum.key[0] as string))
      : xScaleType === 'string'
      ? propsData.filter((datum) => typeof datum.key[0] === 'string')
      : xScaleType === 'Date'
      ? propsData.filter((datum) => datum.key[0] instanceof Date)
      : propsData;

  useEffect(() => {
    cache.current = data;
  });

  const svgRef = useRef<SVGSVGElement>();

  const chartDimensions = sizeToDimension(size!);
  const width = chartDimensions.width;
  const height = chartDimensions.height;
  const margins = {
    top: xAxisVariant === AxisVariant.default || xAxisVariant === AxisVariant.tickValue ? 24 : 8,
    left: yAxisVariant === AxisVariant.default || yAxisVariant === AxisVariant.tickValue ? 48 : 8,
    right: yAxisVariant === AxisVariant.default || yAxisVariant === AxisVariant.tickValue ? 48 : 8,
    bottom: xAxisVariant === AxisVariant.default || xAxisVariant === AxisVariant.tickValue ? 24 : 8,
    ...propsMargins,
  };
  const chartHeight = height - margins.top - margins.bottom;
  const chartWidth = width - margins.left - margins.right;

  const scaleX = (() => {
    if (xScaleType === 'number') {
      const domain = d3extent(data, (d) => d.key[0] as number) as [number, number];
      domain[0] = (xAxisMinValue as number) ?? domain[0];
      domain[1] = (xAxisMaxValue as number) ?? domain[1];
      return d3scaleLinear().domain(domain).range([0, chartWidth]).nice();
    } else if (xScaleType === 'dateString') {
      const domain = d3extent(data, (d) => Date.parse(d.key[0] as string)) as [number, number];
      domain[0] = (xAxisMinValue as number) ?? domain[0];
      domain[1] = (xAxisMaxValue as number) ?? domain[1];
      return d3scaleTime()
        .domain([new Date(domain[0]), new Date(domain[1])])
        .range([0, chartWidth])
        .nice();
    } else if (xScaleType === 'Date') {
      const domain = d3extent(data, (d) => (d.key[0] as Date).valueOf()) as [number, number];
      domain[0] = (xAxisMinValue as number) ?? domain[0];
      domain[1] = (xAxisMaxValue as number) ?? domain[1];
      return d3scaleTime()
        .domain([new Date(domain[0]), new Date(domain[1])])
        .range([0, chartWidth])
        .nice();
    } else {
      const domain =
        xAxisTickValues && Array.isArray(xAxisTickValues)
          ? (xAxisTickValues as string[])
          : data.map((d) => d.key[0] as string);
      domain[0] = (xAxisMinValue as string) ?? domain[0];
      domain[1] = (xAxisMaxValue as string) ?? domain[1];
      return d3scalePoint().domain(domain).range([0, chartWidth]);
    }
  })();
  const scaleY = (() => {
    const domain = d3extent(data, (d) => d.value) as [number, number];
    domain[0] = (yAxisMinValue as number) ?? domain[0];
    domain[1] = (yAxisMaxValue as number) ?? domain[1];
    return d3scaleLinear().domain(domain).range([chartHeight, 0]).nice();
  })();
  const xAxisTickFormat =
    propsXAxisTickFormat ??
    (xScaleType === 'Date' || xScaleType === 'dateString'
      ? (scaleX as d3ScaleTime<number, number, never>).tickFormat()
      : undefined);

  const hasCategory = data[0] && data[0].key.length >= 2 && data[0].key[1] !== null && data[0].key[1] !== undefined;
  const statsByCategory = statsBy(data, sortingMethod!);
  const colorScale = useColor({
    data: statsByCategory,
    colorTheme: colorTheme!,
    category: (d) => d.key as string,
    theme,
  });

  const circles = data
    .sort((a, b) => {
      if (xScaleType === 'number') {
        if (a.key[0] < b.key[0]) return -1;
        if (a.key[0] > b.key[0]) return 1;
        return 0;
      } else if (xScaleType === 'dateString') {
        if (Date.parse(a.key[0] as string) < Date.parse(b.key[0] as string)) return -1;
        if (Date.parse(a.key[0] as string) > Date.parse(b.key[0] as string)) return 1;
        return 0;
      } else if (xScaleType === 'Date') {
        if ((a.key[0] as Date).valueOf() < (b.key[0] as Date).valueOf()) return -1;
        if ((a.key[0] as Date).valueOf() > (b.key[0] as Date).valueOf()) return 1;
        return 0;
      } else if (xScaleType === 'string' && xAxisTickValues && Array.isArray(xAxisTickValues)) {
        if (xAxisTickValues.indexOf(a.key[0]) < xAxisTickValues.indexOf(b.key[0])) return -1;
        if (xAxisTickValues.indexOf(a.key[0]) > xAxisTickValues.indexOf(b.key[0])) return 1;
        return 0;
      }
      return 0;
    })
    .filter((d) => d.value !== null)
    .map((d) => ({
      category: d.key[1] ?? 'default',
      x:
        xScaleType === 'dateString'
          ? dateParser
            ? dateParser(d.key[0] as string)
            : new Date(Date.parse(d.key[0] as string))
          : d.key[0],
      y: d.value,
      data: {
        key: d.key,
        value: d.value,
      },
      points: [d],
    }));
  const lines = hasCategory
    ? Object.values(
        circles.reduce((prev, curr: (typeof circles)[number]) => {
          if (!prev[curr.category!]) {
            prev[curr.category!] = [];
          }
          prev[curr.category!].push(curr);
          return prev;
        }, {} as Record<string, typeof circles>)
      )
    : [circles];

  const createLine = d3line<LinePointDatum>()
    .x((d) => scaleX(d.x as any) as any)
    .y((d) => scaleY(d.y as any));

  const categories: string[] = [];
  const pivotedTable = circles.reduce((acc, item) => {
    const { x, category, y } = item;
    if (!categories.includes(category)) {
      categories.push(category);
    }
    let formattedKey = xAxisTickFormat ? xAxisTickFormat(x as any) : x.toString();
    formattedKey = Array.isArray(formattedKey) ? formattedKey.join(' ') : formattedKey;

    let entry = acc.find((e) => e.key === formattedKey);
    if (!entry) {
      entry = { key: formattedKey };
      acc.push(entry);
    }

    entry[category] = y;

    return acc;
  }, [] as Array<Record<string, string | number | null | undefined> & { key: string }>);

  const { extraLegendItems, ...forwardedLegendProps } = legendProps || {};

  return (
    <StyledLineChart
      mode={typeof onDotClick === 'function' || typeof getDotAnchorProps === 'function' ? 'interactive' : 'static'}
      definition={
        hasCategory
          ? format('definition-linear-with-categories', {
              categoryLength: categories.length,
              circleLength: circles.length,
            })
          : format('definition-linear', { circleLength: circles.length })
      }
      xAxisDefinition={
        xScaleType !== 'string'
          ? format(`x-axis-${xScaleType === 'number' ? 'numbers' : 'dates'}-definition`, {
              numAxis: xAxisPlacement === 'both' ? 2 : 1,
              start: scaleX.domain()[0],
              end: scaleX.domain()[1],
            })
          : format('x-axis-categories-definition', { numAxis: xAxisPlacement === 'both' ? 2 : 1 })
      }
      yAxisDefinition={format('y-axis-numbers-definition', {
        numAxis: yAxisPlacement === 'both' ? 2 : 1,
        start: scaleY.domain()[0],
        end: scaleY.domain()[1],
      })}
      dataTableRepresentation={
        hasCategory
          ? {
              header: (
                <thead>
                  <tr>
                    <th scope="col">{xScaleType === 'Date' || xScaleType === 'dateString' ? 'Date' : 'Key'}</th>
                    {categories.map((category) => (
                      <th key={category} scope="col">
                        {category}
                      </th>
                    ))}
                  </tr>
                </thead>
              ),
              body: (
                <tbody>
                  {pivotedTable.map((row) => (
                    <tr key={row.key}>
                      <th scope="row">{row.key}</th>
                      {categories.map((category) => (
                        <th key={`${row.key}-${category}-${row[category]}`} scope="col">
                          {row[category]}
                        </th>
                      ))}
                    </tr>
                  ))}
                </tbody>
              ),
            }
          : {
              header: (
                <thead>
                  <tr>
                    <th scope="col">{xScaleType === 'Date' || xScaleType === 'dateString' ? 'Date' : 'Key'}</th>
                    <th scope="col">Value</th>
                  </tr>
                </thead>
              ),
              body: (
                <tbody>
                  {circles.map((datum) => {
                    let key = xAxisTickFormat ? xAxisTickFormat(datum.x as any) : datum.x.toString();
                    key = Array.isArray(key) ? key.join(' ') : key;

                    return (
                      <tr key={key}>
                        <th scope="row">{key}</th>
                        <td>{datum.y}</td>
                      </tr>
                    );
                  })}
                </tbody>
              ),
            }
      }
      {...forwardedProps}
      id={id}
      className={className}
      ref={ref as RefObject<HTMLDivElement>}
    >
      <Flexbox flexDirection="column" alignItems="center" gap="8">
        <svg
          ref={svgRef as RefObject<SVGSVGElement>}
          width={width}
          height={height}
          aria-label={
            typeof onDotClick === 'function' || typeof getDotAnchorProps === 'function'
              ? format('interactive-chart')
              : format('static-chart')
          }
          aria-hidden="false"
        >
          <g transform={`translate(${margins.left},${margins.top})`} aria-hidden="true">
            {lines.map((line, index) => {
              return (
                <Line
                  color={colorScale?.(line[0].category!)}
                  data={line.filter((point) => point.y !== null) as any}
                  disableAnimations={disableAnimations}
                  createLine={createLine}
                  key={`line _${index}`}
                />
              );
            })}
          </g>
          {categories.map((category, categoryIndex) => {
            const filteredCircles = circles.filter((circle) => circle.category === category);
            return (
              <g
                key={`${id}-series-${category}`}
                transform={`translate(${margins.left},${margins.top})`}
                aria-hidden="false"
                role="region"
                {...(hasCategory
                  ? {
                      'aria-label': `${category}, category ${categoryIndex + 1} of ${categories.length} with ${
                        filteredCircles.length
                      } circles`,
                    }
                  : {})}
              >
                {filteredCircles.map((circle, index) => {
                  const scaleXDomain = scaleX.domain();
                  const scaleYDomain = scaleY.domain();

                  if (
                    xScaleType === 'number' &&
                    (circle.x! < scaleXDomain[0] ||
                      circle.x! > scaleXDomain[1] ||
                      circle.y! < scaleYDomain[0] ||
                      circle.y! > scaleYDomain[1])
                  ) {
                    return null;
                  }

                  const to = {
                    ...circle,
                    r: 4,
                  };

                  return (
                    <Dot
                      id={`id${id}__circle-${categoryIndex}.${index}`}
                      color={colorScale?.(circle.category!)}
                      data={to}
                      dataset={circles}
                      isSelected={Boolean(isDotSelected?.(to))}
                      anchorProps={getDotAnchorProps ? getDotAnchorProps(to) : undefined}
                      key={`circle _${categoryIndex}.${index}`}
                      labelDecorator={labelDecorator}
                      onClick={onDotClick}
                      role={dotRole}
                      scaleX={scaleX}
                      scaleY={scaleY}
                      tooltipDecorator={tooltipDecorator}
                      tooltipVariant={tooltipVariant}
                      variant={DotVariant.hollow}
                    />
                  );
                })}
              </g>
            );
          })}
          <g transform={`translate(${margins.left},${margins.top})`}>
            {['bottom', 'both'].includes(xAxisPlacement!) ? (
              <Axis
                disableAnimations={disableAnimations}
                position="bottom"
                length={chartWidth}
                scale={scaleX as AnyScale}
                x={0}
                y={chartHeight}
                tickValues={xAxisTickValues ?? 8}
                tickPadding={xAxisTickPadding}
                tickRotation={xAxisTickRotation}
                tickSize={xAxisTickSize}
                tickFormat={xAxisTickFormat}
                tickRemodelling={xAxisTickRemodelling}
                variant={xAxisVariant}
              />
            ) : null}
            {['top', 'both'].includes(xAxisPlacement!) ? (
              <Axis
                disableAnimations={disableAnimations}
                position="top"
                length={chartWidth}
                scale={scaleX as AnyScale}
                x={0}
                y={0}
                tickValues={xAxisTickValues ?? 8}
                tickPadding={xAxisTickPadding}
                tickRotation={xAxisTickRotation}
                tickSize={xAxisTickSize}
                tickFormat={xAxisTickFormat}
                tickRemodelling={xAxisTickRemodelling}
                variant={xAxisVariant}
              />
            ) : null}
            {['left', 'both'].includes(yAxisPlacement!) ? (
              <Axis
                disableAnimations={disableAnimations}
                position="left"
                length={chartHeight}
                scale={scaleY as AnyScale}
                x={0}
                y={chartHeight}
                tickValues={yAxisTickValues ?? 8}
                tickPadding={yAxisTickPadding}
                tickRotation={yAxisTickRotation}
                tickSize={yAxisTickSize}
                tickFormat={yAxisTickFormat}
                tickRemodelling={yAxisTickRemodelling}
                variant={yAxisVariant}
              />
            ) : null}
            {['right', 'both'].includes(yAxisPlacement!) ? (
              <Axis
                disableAnimations={disableAnimations}
                position="right"
                length={chartHeight}
                scale={scaleY as AnyScale}
                x={chartWidth}
                y={chartHeight}
                tickValues={yAxisTickValues ?? 8}
                tickPadding={yAxisTickPadding}
                tickRotation={yAxisTickRotation}
                tickSize={yAxisTickSize}
                tickFormat={yAxisTickFormat}
                tickRemodelling={yAxisTickRemodelling}
                variant={yAxisVariant}
              />
            ) : null}
          </g>
        </svg>
      </Flexbox>
      {hasCategory && legendVariant !== LineChartLegendVariant.none ? (
        <Legend
          data={(extraLegendItems
            ? mergeLegends(extraLegendItems, statsByCategory).sort(getSortingMethod(sortingMethod!))
            : statsByCategory
          ).map((d) => ({
            ...d,
            color: colorScale?.(d.key)!,
          }))}
          variant={
            legendVariant === LineChartLegendVariant.externalLabelValue
              ? LegendVariant.value
              : legendVariant === LineChartLegendVariant.externalLabelPercent
              ? LegendVariant.percent
              : legendVariant === LineChartLegendVariant.externalLabel
              ? LegendVariant.label
              : LegendVariant.custom
          }
          legendDecorator={
            legendDecorator
              ? (datum: LegendItemDatum, props) => legendDecorator(datum as unknown as DotDatum, props)
              : undefined
          }
          {...forwardedLegendProps}
        />
      ) : null}
    </StyledLineChart>
  );
});
